/**
 * EPN-252 — wire types for Contractor Payments records (Payment Processing, docs/contractor-payments-records.md). Dates are `yyyy-MM-dd`; `*Utc` values are
 * ISO-8601 instants. Money is a plain number straight from the server; the screen never derives, "corrects" or totals an amount (YTD and KPIs are the server's).
 */
export const RECORD_STATUSES = ['PendingApproval', 'Approved', 'Paid', 'Failed', 'Voided'] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

export const PAYMENT_METHODS = ['Check', 'DirectDeposit', 'Electronic', 'Cash', 'Other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export type ThresholdState = 'None' | 'Approaching' | 'Reached';
export type Provenance = 'Escochex' | 'Outside Escochex';
export type RecordAction = 'markPaid' | 'void' | 'form1099';

export interface PaymentRecordItem {
  id: string;
  /** Display id, e.g. `CP-1A2B3C4D`: for people to quote. */
  paymentId: string;
  provenance: Provenance;
  contractorId: string;
  contractorName: string;
  businessName: string | null;
  paymentDate: string;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  paymentMethod: PaymentMethod;
  checkNumber: string | null;
  amount: number;
  form1099Trackable: boolean;
  /** Contractor's non-voided Approved + Paid total for the calendar year of THIS row's payment date. */
  ytdTotal: number;
  /** The 1099-trackable portion of `ytdTotal`. */
  ytd1099Total: number;
  /** 1099 alert for this contractor in the calendar year of this row's payment date (server rule; Voided rows are always `None`). */
  threshold1099State: ThresholdState;
  status: RecordStatus;
  hasAttachment: boolean;
  /** Decided by the server for this caller; empty for read-only callers and for Voided rows. */
  allowedActions: RecordAction[];
  version: number;
}

export interface PaymentRecordPage {
  items: PaymentRecordItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface RecordHistoryEntry {
  kind: 'Created' | 'Approved' | 'Paid' | 'Voided';
  atUtc: string;
  byEmail: string | null;
  note: string | null;
}

export interface PaymentRecordDetail extends PaymentRecordItem {
  description: string | null;
  memo: string | null;
  reference: string | null;
  batchReference: string | null;
  amountMode: 'Flat' | 'HoursRate' | null;
  hours: number | null;
  rate: number | null;
  approvedAtUtc: string | null;
  approvedByEmail: string | null;
  paid: { paidOn: string; reference: string | null; atUtc: string; byEmail: string } | null;
  void: { reason: string; atUtc: string; byEmail: string } | null;
  recordedByEmail: string | null;
  recordedAtUtc: string;
  history: RecordHistoryEntry[];
}

export interface PaymentRecordSummary {
  year: number;
  totalContractorPaymentsYtd: number;
  paymentCount: number;
  activeContractors: number;
  missingW9: number;
  /** Company setting; `null` = none configured, so the card says "Threshold not set". */
  form1099ThresholdAmount: number | null;
  form1099ThresholdConfigured: boolean;
  /** EFFECTIVE approaching percent (company setting, else the server default); `null` when no threshold is configured. */
  form1099ApproachingPercent: number | null;
  /** Contractors whose counted 1099-trackable year total is at or above the threshold. */
  thresholdReachedCount: number;
  /** Contractors at or above threshold x percent and below the threshold. */
  thresholdApproachingCount: number;
}

export interface StatementLine {
  description: string;
  amountMode: 'Flat' | 'HoursRate';
  hours: number | null;
  rate: number | null;
  amount: number;
}

export interface StatementCompany {
  name: string | null;
  legalName?: string | null;
  address?: string | null;
}

export interface PaymentStatement {
  title: string;
  /** BACKEND GAP: null until Payment Processing has a company profile source; the page then falls back to the signed-in company name. */
  company: StatementCompany | null;
  paymentId: string;
  provenance: Provenance;
  status: RecordStatus;
  isPreview: boolean;
  contractor: { name: string; businessName: string | null };
  paymentDate: string;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentMethodLabel: string;
  /** Present only for method Check. */
  checkNumber: string | null;
  description: string | null;
  memo: string | null;
  reference: string | null;
  batchReference: string | null;
  lines: StatementLine[];
  paid: { paidOn: string; reference: string | null } | null;
  void: { reason: string; byEmail: string; atUtc: string } | null;
  notACheckWatermark: boolean;
  voidedWatermarkCount: number;
  generatedAtUtc: string;
}

export type SortField = 'paymentDate' | 'contractor' | 'servicePeriod' | 'paymentMethod' | 'amount' | 'form1099' | 'status';
export type SortDir = 'asc' | 'desc';

/** Filters and ordering the server applies (the list and both exports take the same set). */
export interface RecordQuery {
  page: number;
  pageSize: number;
  sort: SortField;
  dir: SortDir;
  search?: string;
  contractorId?: string;
  status?: RecordStatus;
  paymentMethod?: PaymentMethod;
  form1099Trackable?: boolean;
  provenance?: Provenance;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  servicePeriodFrom?: string;
  servicePeriodTo?: string;
  year?: number;
}

export interface OutsidePaymentInput {
  contractorId: string;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  servicePeriodStart: string;
  servicePeriodEnd: string;
  amount: number;
  memo: string;
  reference: string;
  checkNumber: string;
  form1099Trackable: boolean;
  recordAnyway: boolean;
  file: File | null;
}

export interface ExportedFile {
  blob: Blob;
  fileName: string;
}

export type ExportFormat = 'csv' | 'xlsx';
